import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/hooks/use-auth";
import {
  CheckCircle, XCircle, Camera, CameraOff, Loader2, PoundSterling, LogIn,
  ChevronLeft, ImageIcon, RefreshCw,
} from "lucide-react";

type Store = { id: number; name: string; staffRole: string };

type ScanResult = {
  barcode: string;
  status: "found" | "already" | "notfound" | "error";
  productName?: string;
  section?: string;
  pricePence?: number;
};

type EditorState = {
  barcode: string;
  storeProductId: number | null;
  name: string;
  description: string;
  pricePence: number | null;
  imageUrl: string;
  groceryProductId: number | null;
  isNew: boolean; // true if just created vs already existed
  ageRestricted?: boolean;
};

type SaveStatus = "idle" | "saving" | "saved" | "error" | "offline";

type QueueEntry = { storeId: number; productId: number; patch: Record<string, unknown> };

function parsePrice(str: string): number | null {
  const clean = str.replace(/[^0-9.]/g, "");
  const val = parseFloat(clean);
  if (isNaN(val) || val < 0) return null;
  return Math.round(val * 100);
}

function pricePenceToDisplay(pence: number | null): string {
  if (pence === null || pence === 0) return "";
  return (pence / 100).toFixed(2);
}

// ─── Image compression ───────────────────────────────────────────────────────

async function compressImage(file: File, maxPx = 1200, quality = 0.82): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxPx || height > maxPx) {
        const scale = maxPx / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) { resolve(file); return; }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(file); return; }
          resolve(new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }));
        },
        "image/jpeg",
        quality,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Failed to load image")); };
    img.src = url;
  });
}

async function uploadFile(file: File, token: string): Promise<string> {
  const reqRes = await fetch("/api/storage/uploads/request-url", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type }),
  });
  if (!reqRes.ok) throw new Error("Failed to get upload URL");
  const { uploadURL, objectPath, finalizeToken } = await reqRes.json() as { uploadURL: string; objectPath: string; finalizeToken: string };
  const uploadRes = await fetch(uploadURL, { method: "PUT", body: file, headers: { "Content-Type": file.type, Authorization: `Bearer ${token}` } });
  if (!uploadRes.ok) throw new Error("Upload failed");
  const finalizeRes = await fetch("/api/storage/uploads/finalize", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ finalizeToken }),
  });
  if (!finalizeRes.ok) throw new Error("Failed to finalize upload");
  return `/api/storage/objects${objectPath.startsWith("/objects") ? objectPath.slice(8) : objectPath}`;
}

// ─── Offline queue helpers ───────────────────────────────────────────────────

const QUEUE_KEY = "ah_product_save_queue";

function loadQueue(): QueueEntry[] {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? "[]") as QueueEntry[]; }
  catch { return []; }
}

function saveQueue(q: QueueEntry[]) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
}

function enqueue(entry: QueueEntry) {
  const q = loadQueue();
  q.push(entry);
  saveQueue(q);
}

async function fetchOfd(barcode: string): Promise<{ name: string; imageUrl: string } | null> {
  try {
    const controller = new AbortController();
    const tid = setTimeout(() => controller.abort(), 4000);
    const res = await fetch(
      `https://world.openfoodfacts.org/api/v2/product/${encodeURIComponent(barcode)}.json?fields=product_name,brands,image_front_url,generic_name`,
      { signal: controller.signal },
    );
    clearTimeout(tid);
    if (!res.ok) return null;
    const data = await res.json() as { status: number; product?: { product_name?: string; brands?: string; image_front_url?: string; generic_name?: string } };
    if (data.status !== 1 || !data.product) return null;
    const p = data.product;
    const parts = [p.brands, p.product_name].filter(Boolean);
    const name = parts.length > 0 ? parts.join(" ") : (p.generic_name ?? "");
    return name ? { name, imageUrl: p.image_front_url ?? "" } : null;
  } catch {
    return null;
  }
}

function beep() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 1400;
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.12);
    osc.start();
    osc.stop(ctx.currentTime + 0.12);
  } catch { /* AudioContext blocked */ }
  navigator.vibrate?.(40);
}

async function flushQueue(token: string) {
  const q = loadQueue();
  if (q.length === 0) return;
  const remaining: QueueEntry[] = [];
  for (const entry of q) {
    try {
      const res = await fetch(`/api/stores/${entry.storeId}/products/${entry.productId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(entry.patch),
      });
      if (!res.ok) remaining.push(entry);
    } catch {
      remaining.push(entry);
    }
  }
  saveQueue(remaining);
}

// ─── Product Editor ──────────────────────────────────────────────────────────

interface EditorProps {
  editor: EditorState;
  storeId: number;
  token: string;
  onClose: (productName: string, isNew: boolean) => void;
  onProductCreated: (id: number) => void;
}

function ProductEditor({ editor: initialEditor, storeId, token, onClose, onProductCreated }: EditorProps) {
  const [name, setName] = useState(initialEditor.name);
  const [description, setDescription] = useState(initialEditor.description);
  const [priceDisplay, setPriceDisplay] = useState(pricePenceToDisplay(initialEditor.pricePence));
  const [priceError, setPriceError] = useState("");
  const [imageUrl, setImageUrl] = useState(initialEditor.imageUrl);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [storeProductId, setStoreProductId] = useState<number | null>(initialEditor.storeProductId);
  const [pricePence, setPricePence] = useState<number | null>(initialEditor.pricePence);
  const [ageRestricted, setAgeRestricted] = useState(initialEditor.ageRestricted ?? false);

  const nameRef = useRef(name);
  const descRef = useRef(description);
  const priceRef = useRef(pricePence);
  const imageUrlRef = useRef(imageUrl);
  const storeProductIdRef = useRef(storeProductId);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const priceInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { nameRef.current = name; }, [name]);
  useEffect(() => { descRef.current = description; }, [description]);
  useEffect(() => { priceRef.current = pricePence; }, [pricePence]);
  useEffect(() => { imageUrlRef.current = imageUrl; }, [imageUrl]);
  useEffect(() => { storeProductIdRef.current = storeProductId; }, [storeProductId]);

  // Flush offline queue on reconnect
  useEffect(() => {
    const handler = () => { void flushQueue(token); };
    window.addEventListener("online", handler);
    return () => window.removeEventListener("online", handler);
  }, [token]);

  // Auto-focus price field when product name is already known (catalogue/OFD hit)
  useEffect(() => {
    if (initialEditor.name) {
      const t = setTimeout(() => priceInputRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buildPatch = useCallback(() => ({
    name: nameRef.current,
    description: descRef.current || null,
    pricePence: priceRef.current ?? 0,
    imageUrl: imageUrlRef.current || null,
  }), []);

  const patchProduct = useCallback(async (patch: Record<string, unknown>) => {
    const pid = storeProductIdRef.current;
    if (!pid) return;
    if (!navigator.onLine) {
      enqueue({ storeId, productId: pid, patch });
      setSaveStatus("offline");
      return;
    }
    setSaveStatus("saving");
    try {
      const res = await fetch(`/api/stores/${storeId}/products/${pid}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error("Patch failed");
      setSaveStatus("saved");
    } catch {
      setSaveStatus("error");
    }
  }, [storeId, token]);

  const ensureProductCreated = useCallback(async (): Promise<number | null> => {
    if (storeProductIdRef.current) return storeProductIdRef.current;
    setSaveStatus("saving");
    try {
      const res = await fetch(`/api/stores/${storeId}/products`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          groceryProductId: initialEditor.groceryProductId,
          barcode: initialEditor.barcode,
          name: nameRef.current || initialEditor.barcode,
          description: descRef.current || null,
          pricePence: priceRef.current ?? 0,
          imageUrl: imageUrlRef.current || null,
        }),
      });

      if (res.status === 409) {
        // Already exists — look it up
        const lookupRes = await fetch(`/api/stores/${storeId}/products/barcode/${encodeURIComponent(initialEditor.barcode)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (lookupRes.ok) {
          const d = await lookupRes.json() as { found: boolean; product?: { id: number } };
          if (d.found && d.product) {
            setStoreProductId(d.product.id);
            onProductCreated(d.product.id);
            setSaveStatus("saved");
            return d.product.id;
          }
        }
        setSaveStatus("error");
        return null;
      }

      if (!res.ok) throw new Error("Create failed");
      const d = await res.json() as { product: { id: number } };
      setStoreProductId(d.product.id);
      onProductCreated(d.product.id);
      setSaveStatus("saved");
      return d.product.id;
    } catch {
      setSaveStatus("error");
      return null;
    }
  }, [storeId, token, initialEditor.groceryProductId, initialEditor.barcode, onProductCreated]);

  const scheduleSave = useCallback(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      const pid = await ensureProductCreated();
      if (pid) void patchProduct(buildPatch());
    }, 600);
  }, [ensureProductCreated, patchProduct, buildPatch]);

  const handleNameChange = (v: string) => {
    setName(v);
    scheduleSave();
  };

  const handleDescriptionChange = (v: string) => {
    setDescription(v);
    scheduleSave();
  };

  const handlePriceBlur = async () => {
    if (!priceDisplay) {
      setPricePence(null);
      void (async () => {
        const pid = await ensureProductCreated();
        if (pid) void patchProduct({ ...buildPatch(), pricePence: 0 });
      })();
      return;
    }
    const parsed = parsePrice(priceDisplay);
    if (parsed === null) {
      setPriceError("Enter a valid price e.g. 1.99");
      return;
    }
    setPriceError("");
    setPricePence(parsed);
    setPriceDisplay((parsed / 100).toFixed(2));
    void (async () => {
      const pid = await ensureProductCreated();
      if (pid) void patchProduct({ ...buildPatch(), pricePence: parsed });
    })();
  };

  const handleImageFile = async (file: File) => {
    if (file.size > 20 * 1024 * 1024) return;
    // Show local preview immediately
    const preview = URL.createObjectURL(file);
    setLocalPreview(preview);
    setImageUploading(true);
    try {
      const compressed = await compressImage(file);
      const url = await uploadFile(compressed, token);
      setImageUrl(url);
      imageUrlRef.current = url;
      URL.revokeObjectURL(preview);
      setLocalPreview(null);
      setImageUploading(false);
      // Save with new image
      const pid = await ensureProductCreated();
      if (pid) void patchProduct({ ...buildPatch(), imageUrl: url });
    } catch {
      setImageUploading(false);
      URL.revokeObjectURL(preview);
      setLocalPreview(null);
      setSaveStatus("error");
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleImageFile(file);
    e.target.value = "";
  };

  const handleRetry = () => {
    setSaveStatus("saving");
    void patchProduct(buildPatch());
  };

  const handleScanNext = async () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    // Flush any pending changes
    const pid = await ensureProductCreated();
    if (pid && (saveStatus === "idle" || saveStatus === "error")) {
      await patchProduct(buildPatch());
    }
    onClose(name || initialEditor.barcode, initialEditor.isNew);
  };

  const [editorImgError, setEditorImgError] = useState(false);
  useEffect(() => { setEditorImgError(false); }, [imageUrl]);
  const displayImage = localPreview ?? (editorImgError ? "" : imageUrl);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, background: "white", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div className="bg-[#1e3a5f] text-white px-4 py-3 flex items-center gap-3" style={{ flexShrink: 0 }}>
        <button
          onClick={() => void handleScanNext()}
          className="flex items-center gap-1.5 text-sm font-medium text-white/80 hover:text-white min-h-[44px] min-w-[44px] -ml-1 px-1"
          aria-label="Back to scanner"
        >
          <ChevronLeft size={20} />
          <span>Back</span>
        </button>
        <div className="flex-1 text-center text-sm font-semibold">{initialEditor.isNew ? "New Product" : "Edit Product"}</div>
        <div className="min-w-[60px] text-right">
          {saveStatus === "saving" && (
            <span className="flex items-center gap-1 text-xs text-white/70 justify-end">
              <Loader2 size={12} className="animate-spin" /> Saving
            </span>
          )}
          {saveStatus === "saved" && (
            <span className="flex items-center gap-1 text-xs text-green-300 justify-end">
              <CheckCircle size={12} /> Saved
            </span>
          )}
          {saveStatus === "error" && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-1 text-xs text-red-300 hover:text-red-200 justify-end"
            >
              <RefreshCw size={12} /> Retry
            </button>
          )}
          {saveStatus === "offline" && (
            <span className="text-xs text-amber-300">Queued</span>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto" }}>
      <div className="max-w-lg mx-auto px-5 py-5 space-y-5">

        {/* Product image */}
        <div className="space-y-2">
          {displayImage ? (
            <div className="relative w-full aspect-square max-w-[200px] mx-auto">
              <img
                src={displayImage}
                alt="Product"
                onError={() => setEditorImgError(true)}
                className="w-full h-full object-cover rounded-xl border border-gray-200"
              />
              {imageUploading && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-xl">
                  <Loader2 size={28} className="animate-spin text-white" />
                </div>
              )}
            </div>
          ) : (
            <div className="w-[200px] mx-auto aspect-square bg-gray-100 rounded-xl border border-gray-200 flex items-center justify-center">
              {imageUploading
                ? <Loader2 size={28} className="animate-spin text-gray-400" />
                : <ImageIcon size={40} className="text-gray-300" />
              }
            </div>
          )}

          {/* Image upload buttons */}
          <div className="flex gap-2 justify-center">
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleFileInput}
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileInput}
            />
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              disabled={imageUploading}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-[#1e3a5f] text-white text-sm font-medium rounded-xl min-h-[44px] disabled:opacity-50"
            >
              <Camera size={16} />
              Take Photo
            </button>
            <button
              type="button"
              onClick={() => galleryInputRef.current?.click()}
              disabled={imageUploading}
              className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl min-h-[44px] disabled:opacity-50"
            >
              <ImageIcon size={16} />
              Gallery
            </button>
          </div>
        </div>

        {/* Product name */}
        <div className="space-y-1.5">
          <label className="block text-sm font-semibold text-gray-700">Product Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Enter product name"
            style={{ fontSize: "16px" }}
            className="w-full border border-gray-300 rounded-xl px-3 py-3 outline-none focus:ring-2 focus:ring-[#1e3a5f]/30 focus:border-[#1e3a5f] min-h-[44px]"
          />
        </div>

        {/* Price */}
        <div className="space-y-1.5">
          <label className="block text-sm font-semibold text-gray-700 flex items-center gap-1">
            <PoundSterling size={14} />
            Price
          </label>
          <div className="flex items-center gap-1 border border-gray-300 rounded-xl px-3 focus-within:ring-2 focus-within:ring-[#1e3a5f]/30 focus-within:border-[#1e3a5f] min-h-[44px]">
            <span className="text-gray-500 font-medium">£</span>
            <input
              type="text"
              inputMode="decimal"
              value={priceDisplay}
              onChange={(e) => {
                // Strip £ sign if user pastes it
                const stripped = e.target.value.replace(/£/g, "");
                setPriceDisplay(stripped);
                setPriceError("");
              }}
              ref={priceInputRef}
              onBlur={() => void handlePriceBlur()}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void handleScanNext(); } }}
              placeholder="0.00"
              style={{ fontSize: "16px" }}
              className="flex-1 bg-transparent outline-none py-3 min-h-[44px]"
            />
          </div>
          {priceError && <p className="text-xs text-red-500">{priceError}</p>}
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <label className="block text-sm font-semibold text-gray-700">
            Description <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            placeholder="Add product description, ingredients, or notes…"
            rows={3}
            style={{ fontSize: "16px" }}
            className="w-full border border-gray-300 rounded-xl px-3 py-3 outline-none focus:ring-2 focus:ring-[#1e3a5f]/30 focus:border-[#1e3a5f] resize-none"
          />
        </div>

        {/* Age restriction toggle */}
        <div>
          <button
            type="button"
            onClick={async () => {
              const next = !ageRestricted;
              setAgeRestricted(next);
              const pid = await ensureProductCreated();
              if (pid) void patchProduct({ ...buildPatch(), ageRestricted: next, minimumAge: 18 });
            }}
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl border-2 transition-colors ${
              ageRestricted
                ? "border-red-500 bg-red-50 text-red-700"
                : "border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300"
            }`}
          >
            <span style={{ fontSize: 22 }}>🔞</span>
            <div className="flex-1 text-left">
              <div className="text-sm font-semibold">{ageRestricted ? "Age Restricted (18+)" : "Mark as Age Restricted"}</div>
              <div className="text-xs font-normal opacity-70">Tobacco, alcohol, vapes, knives</div>
            </div>
            <div className={`w-11 h-6 rounded-full transition-colors relative ${ageRestricted ? "bg-red-500" : "bg-gray-300"}`}>
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${ageRestricted ? "translate-x-5" : "translate-x-0.5"}`} />
            </div>
          </button>
        </div>

        {/* Barcode */}
        <div className="text-xs text-gray-400 font-mono">
          Barcode: {initialEditor.barcode}
        </div>

      </div>
      </div>

      {/* Sticky footer - always visible Scan Next */}
      <div style={{ flexShrink: 0, padding: "12px 20px", background: "white", borderTop: "1px solid #e5e7eb", paddingBottom: "calc(12px + env(safe-area-inset-bottom))" }}>
        <button
          onClick={() => void handleScanNext()}
          className="w-full py-4 bg-[#f5a623] text-white font-bold text-base rounded-xl min-h-[52px] hover:bg-[#e09516] active:bg-[#c8841a] flex items-center justify-center gap-2"
        >
          Scan Next Product →
        </button>
      </div>
    </div>
  );
}

// ─── Quick Save card ─────────────────────────────────────────────────────────

type QuickCardState = {
  barcode: string;
  storeProductId: number | null;
  groceryProductId: number | null;
  name: string;
  imageUrl: string;
  pricePence: number | null;
  isNew: boolean;
};

function QuickCard({ card, storeId, token, onSave, onEdit }: {
  card: QuickCardState;
  storeId: number;
  token: string;
  onSave: (name: string, pricePence: number, wasNew: boolean) => void;
  onEdit: () => void;
}) {
  const [priceDisplay, setPriceDisplay] = useState(pricePenceToDisplay(card.pricePence));
  const [imageUrl, setImageUrl] = useState(card.imageUrl);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const priceRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const imageUrlRef = useRef(imageUrl);
  const [imgError, setImgError] = useState(false);
  useEffect(() => { imageUrlRef.current = imageUrl; setImgError(false); }, [imageUrl]);

  useEffect(() => {
    const t = setTimeout(() => priceRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, []);

  const handleImageFile = async (file: File) => {
    if (file.size > 20 * 1024 * 1024) return;
    const preview = URL.createObjectURL(file);
    setLocalPreview(preview);
    setImageUploading(true);
    try {
      const compressed = await compressImage(file);
      const url = await uploadFile(compressed, token);
      setImageUrl(url);
      imageUrlRef.current = url;
      URL.revokeObjectURL(preview);
      setLocalPreview(null);
      // If product already exists, patch the image immediately
      if (card.storeProductId) {
        await fetch(`/api/stores/${storeId}/products/${card.storeProductId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ imageUrl: url }),
        });
      }
    } catch {
      URL.revokeObjectURL(preview);
      setLocalPreview(null);
    } finally {
      setImageUploading(false);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleImageFile(file);
    e.target.value = "";
  };

  const doSave = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    const pence = parsePrice(priceDisplay) ?? 0;
    const currentImageUrl = imageUrlRef.current;
    try {
      if (card.storeProductId) {
        const res = await fetch(`/api/stores/${storeId}/products/${card.storeProductId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ pricePence: pence, imageUrl: currentImageUrl || null }),
        });
        if (!res.ok) throw new Error();
      } else {
        const res = await fetch(`/api/stores/${storeId}/products`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            barcode: card.barcode,
            groceryProductId: card.groceryProductId,
            name: card.name || card.barcode,
            pricePence: pence,
            imageUrl: currentImageUrl || null,
          }),
        });
        if (!res.ok && res.status !== 409) throw new Error();
      }
      beep();
      onSave(card.name || card.barcode, pence, card.isNew);
    } catch {
      setError("Save failed — tap retry");
      setSaving(false);
    }
  };

  const displayImage = localPreview ?? imageUrl;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.82)", display: "flex", alignItems: "flex-end" }}>
      <div style={{ background: "white", width: "100%", borderRadius: "24px 24px 0 0", maxHeight: "92vh", overflowY: "auto", paddingBottom: "env(safe-area-inset-bottom)" }}>
        {/* drag handle */}
        <div style={{ width: 44, height: 4, background: "#e5e7eb", borderRadius: 2, margin: "16px auto 14px" }} />

        <div style={{ padding: "0 20px 20px" }}>

          {/* Product image — large, tappable */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ position: "relative", width: "100%", aspectRatio: "1", maxHeight: 200, overflow: "hidden", borderRadius: 16, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
              {displayImage ? (
                <img
                  src={displayImage}
                  alt=""
                  onError={() => setImgError(true)}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <ImageIcon size={48} color="#d1d5db" />
              )}
              {imageUploading && (
                <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 16 }}>
                  <Loader2 size={32} color="white" className="animate-spin" />
                </div>
              )}
            </div>

            {/* Camera / Gallery buttons */}
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileInput} />
            <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={handleFileInput} />
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => cameraRef.current?.click()}
                disabled={imageUploading}
                style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 0", background: "#1e3a5f", color: "white", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: imageUploading ? 0.5 : 1 }}
              >
                <Camera size={15} /> Take Photo
              </button>
              <button
                type="button"
                onClick={() => galleryRef.current?.click()}
                disabled={imageUploading}
                style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 0", background: "white", color: "#374151", border: "1.5px solid #d1d5db", borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: imageUploading ? 0.5 : 1 }}
              >
                <ImageIcon size={15} /> Gallery
              </button>
            </div>
          </div>

          {/* Product name + barcode */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#111827", lineHeight: 1.3 }}>{card.name || card.barcode}</div>
            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 3 }}>
              {card.isNew ? "New product" : "Update"} · <span style={{ fontFamily: "monospace" }}>{card.barcode}</span>
            </div>
          </div>

          {/* Price input */}
          <div style={{ background: "#f0f7ff", borderRadius: 14, padding: "10px 16px", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 30, fontWeight: 900, color: "#1e3a5f", lineHeight: 1 }}>£</span>
            <input
              ref={priceRef}
              type="text"
              inputMode="decimal"
              value={priceDisplay}
              onChange={(e) => setPriceDisplay(e.target.value.replace(/£/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void doSave(); } }}
              placeholder="0.00"
              style={{ flex: 1, fontSize: 36, fontWeight: 900, background: "transparent", border: "none", outline: "none", color: "#1e3a5f", lineHeight: 1, padding: "4px 0" }}
            />
          </div>

          {error && <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 10 }}>{error}</div>}

          <button
            onClick={() => void doSave()}
            disabled={saving || imageUploading}
            style={{ width: "100%", background: (saving || imageUploading) ? "#86efac" : "#22c55e", color: "white", fontWeight: 800, fontSize: 18, padding: "16px 24px", borderRadius: 16, border: "none", cursor: (saving || imageUploading) ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 10 }}
          >
            {saving ? <><Loader2 size={20} className="animate-spin" /> Saving…</>
              : imageUploading ? <><Loader2 size={20} className="animate-spin" /> Uploading image…</>
              : "✓ Save & Scan Next"}
          </button>

          <button
            onClick={onEdit}
            style={{ width: "100%", background: "transparent", color: "#6b7280", fontSize: 14, padding: "10px", border: "none", cursor: "pointer" }}
          >
            Edit details (description, 18+ flag)
          </button>

        </div>
      </div>
    </div>
  );
}

// ─── Skeleton for barcode lookup ─────────────────────────────────────────────

function EditorSkeleton({ barcode }: { barcode: string }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, background: "white", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="bg-[#1e3a5f] text-white px-4 py-3 flex items-center gap-3">
        <div className="h-5 w-16 rounded bg-white/20 animate-pulse" />
        <div className="flex-1 h-4 w-24 mx-auto rounded bg-white/20 animate-pulse" />
      </div>
      <div className="max-w-lg mx-auto px-5 py-5 space-y-5">
        <div className="w-[200px] mx-auto aspect-square bg-gray-100 rounded-xl animate-pulse" />
        <div className="h-12 rounded-xl bg-gray-100 animate-pulse" />
        <div className="h-12 rounded-xl bg-gray-100 animate-pulse" />
        <div className="h-24 rounded-xl bg-gray-100 animate-pulse" />
        <div className="text-xs text-gray-300 font-mono">Barcode: {barcode}</div>
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function StoreScanner() {
  const { user } = useAuth();
  const token = localStorage.getItem("ayrshire_token");

  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [scanning, setScanning] = useState(false);
  const [log, setLog] = useState<ScanResult[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [quickCard, setQuickCard] = useState<QuickCardState | null>(null);
  const [editorLoading, setEditorLoading] = useState<string | null>(null); // barcode being looked up

  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<{ stop: () => void } | null>(null);
  const scanningRef = useRef(false);
  const lastScannedRef = useRef<string>("");
  const lastScannedTimeRef = useRef<number>(0);
  const manualRef = useRef<HTMLInputElement>(null);
  const priceValRef = useRef(priceInput);
  useEffect(() => { priceValRef.current = priceInput; }, [priceInput]);

  useEffect(() => {
    if (!token) return;
    setLoadingStores(true);
    fetch("/api/order/my-stores", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d: { items?: Store[] }) => {
        const list = d.items ?? [];
        setStores(list);
        if (list.length === 1) setSelectedStoreId(list[0].id);
      })
      .catch(() => {})
      .finally(() => setLoadingStores(false));
  }, [token]);

  // Flush offline queue on reconnect
  useEffect(() => {
    if (!token) return;
    const handler = () => { void flushQueue(token); };
    window.addEventListener("online", handler);
    // Try to flush on mount too
    if (navigator.onLine) void flushQueue(token);
    return () => window.removeEventListener("online", handler);
  }, [token]);

  const handleBarcode = useCallback(async (barcode: string) => {
    if (!selectedStoreId || scanningRef.current) return;
    const clean = barcode.trim();
    if (!clean) return;

    // Block scanner while editor is loading or open
    scanningRef.current = true;
    setScanning(true);
    setEditorLoading(clean);

    // Parallel lookups: catalogue product + existing store product
    type CatProduct = { id: number; name: string; brand: string | null; quantity: string | null; imageUrl: string | null; categories: string[]; barcode: string };
    type StoreProduct = { id: number; name: string; description: string | null; pricePence: number; imageUrl: string | null; groceryProductId: number | null };
    let catalogueProduct: CatProduct | null = null;
    let existingStoreProduct: StoreProduct | null = null;

    try {
      const [catRes, storeRes] = await Promise.all([
        fetch(`/api/grocery/barcode/${encodeURIComponent(clean)}`),
        token
          ? fetch(`/api/stores/${selectedStoreId}/products/barcode/${encodeURIComponent(clean)}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
          : Promise.resolve(null),
      ]);
      if (catRes.ok) catalogueProduct = (await catRes.json() as { item: CatProduct }).item;
      if (storeRes?.ok) {
        const d = await storeRes.json() as { found: boolean; product?: StoreProduct };
        if (d.found && d.product) existingStoreProduct = d.product;
      }
    } catch { /* continue with nulls */ }

    setScanning(false);
    setEditorLoading(null);

    const priceFromInput = parsePrice(priceValRef.current);

    // Not in our catalogue or store — try Open Food Facts for name + image
    if (!catalogueProduct && !existingStoreProduct) {
      const ofd = await fetchOfd(clean);
      if (ofd) {
        // OFD found it — show QuickCard so staff just confirm price and move on
        setQuickCard({
          barcode: clean,
          storeProductId: null,
          groceryProductId: null,
          name: ofd.name,
          imageUrl: ofd.imageUrl,
          pricePence: priceFromInput,
          isNew: true,
        });
      } else {
        // Completely unknown — full editor for manual entry
        setEditor({
          barcode: clean,
          storeProductId: null,
          groceryProductId: null,
          name: "",
          description: "",
          pricePence: priceFromInput,
          imageUrl: "",
          isNew: true,
        });
      }
      return;
    }

    // Found in our catalogue or store — show QuickCard for fast confirmation
    const initialPricePence: number | null =
      priceFromInput ?? (existingStoreProduct?.pricePence ?? null);
    const rawName = existingStoreProduct?.name ?? catalogueProduct?.name ?? clean;
    const rawImage = existingStoreProduct?.imageUrl ?? catalogueProduct?.imageUrl ?? "";

    setQuickCard({
      barcode: clean,
      storeProductId: existingStoreProduct?.id ?? null,
      groceryProductId: existingStoreProduct?.groceryProductId ?? catalogueProduct?.id ?? null,
      name: rawName,
      imageUrl: rawImage,
      pricePence: initialPricePence && initialPricePence > 0 ? initialPricePence : null,
      isNew: existingStoreProduct === null,
    });
    // scanningRef stays true while quickCard/editor is open
  }, [selectedStoreId, token]);

  const handleEditorClose = useCallback((productName: string, wasNew: boolean) => {
    // Add to log
    setLog((prev) => [{
      barcode: editor?.barcode ?? "",
      status: wasNew ? "found" : "already",
      productName,
      pricePence: editor?.pricePence ?? undefined,
    }, ...prev]);
    setEditor(null);
    // Reset so the same barcode doesn't re-trigger immediately
    lastScannedTimeRef.current = Date.now() - 2000; // allow re-scan after 500ms
    scanningRef.current = false;
  }, [editor]);

  const handleProductCreated = useCallback((_id: number) => {
    // storeProductId is managed inside ProductEditor, nothing needed here
  }, []);

  const handleQuickCardSave = useCallback((productName: string, pricePence: number, wasNew: boolean) => {
    const barcode = quickCard?.barcode ?? "";
    setLog((prev) => [{ barcode, status: wasNew ? "found" : "already", productName, pricePence: pricePence || undefined }, ...prev]);
    setQuickCard(null);
    lastScannedTimeRef.current = Date.now() - 2000;
    scanningRef.current = false;
  }, [quickCard]);

  const handleQuickCardEdit = useCallback(() => {
    if (!quickCard) return;
    setEditor({
      barcode: quickCard.barcode,
      storeProductId: quickCard.storeProductId,
      groceryProductId: quickCard.groceryProductId,
      name: quickCard.name,
      description: "",
      pricePence: quickCard.pricePence,
      imageUrl: quickCard.imageUrl,
      isNew: quickCard.isNew,
    });
    setQuickCard(null);
  }, [quickCard]);

  // Keep a ref so the ZXing callback always calls the latest handleBarcode
  const handleBarcodeRef = useRef(handleBarcode);
  useEffect(() => { handleBarcodeRef.current = handleBarcode; }, [handleBarcode]);

  const startCamera = useCallback(async () => {
    if (!videoRef.current) return;
    try {
      // Dynamic CDN import — works on all browsers including iOS Chrome/Safari
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      // @ts-ignore — esm.sh CDN import, no type declarations available
      const { BrowserMultiFormatReader } = await import(/* @vite-ignore */ "https://esm.sh/@zxing/browser@0.1.4") as any;
      const reader = new BrowserMultiFormatReader();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const controls = await reader.decodeFromVideoDevice(undefined, videoRef.current, (result: any) => {
        if (!result) return;
        const barcode = result.getText() as string;
        const now = Date.now();
        if (barcode !== lastScannedRef.current || now - lastScannedTimeRef.current > 2500) {
          lastScannedRef.current = barcode;
          lastScannedTimeRef.current = now;
          void handleBarcodeRef.current(barcode);
        }
      });
      controlsRef.current = controls;
      setCameraOn(true);
    } catch {
      alert("Could not access camera. Please check browser permissions, or use the barcode input below.");
    }
  }, []);

  const stopCamera = useCallback(() => {
    controlsRef.current?.stop();
    controlsRef.current = null;
    setCameraOn(false);
  }, []);

  useEffect(() => () => stopCamera(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const onManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const val = manualInput.trim();
    if (val) {
      void handleBarcode(val);
      setManualInput("");
      setTimeout(() => manualRef.current?.focus(), 50);
    }
  };

  if (!token || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center max-w-sm w-full">
          <div className="w-14 h-14 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center mx-auto mb-4">
            <LogIn size={24} className="text-primary" />
          </div>
          <h1 className="text-xl font-bold text-foreground mb-2">Sign in to scan products</h1>
          <p className="text-sm text-gray-500 mb-6">Log in with your Ayrshire Hub account to add products to your store.</p>
          <a
            href="/login?next=/store/scan"
            className="block w-full py-3 bg-[#1e3a5f] text-white font-semibold rounded-xl text-sm hover:bg-[#162d4a]"
          >
            Sign in
          </a>
        </div>
      </div>
    );
  }

  const selectedStore = stores.find((s) => s.id === selectedStoreId);
  const foundCount = log.filter((r) => r.status === "found").length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-[#1e3a5f] text-white px-6 py-4 flex items-center gap-3">
        <div className="flex-1">
          <div className="text-xs text-white/60 font-medium uppercase tracking-wide">Store Back Office</div>
          <h1 className="text-lg font-bold">Product Scanner</h1>
        </div>
        {selectedStore && (
          <div className="text-right">
            <div className="text-xs text-white/60">Scanning for</div>
            <div className="text-sm font-semibold">{selectedStore.name}</div>
          </div>
        )}
      </div>

      <div className="max-w-lg mx-auto p-5 space-y-5">

        {loadingStores ? (
          <div className="flex items-center gap-2 text-sm text-gray-400 py-4">
            <Loader2 size={16} className="animate-spin" /> Loading your stores…
          </div>
        ) : stores.length === 0 ? (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 dark:text-amber-300">
            No stores found for your account. Contact support if this is unexpected.
          </div>
        ) : stores.length > 1 ? (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Select store</label>
            <select
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white"
              value={selectedStoreId ?? ""}
              onChange={(e) => { setSelectedStoreId(Number(e.target.value) || null); setLog([]); }}
            >
              <option value="">— Choose a store —</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        ) : null}

        {selectedStoreId && (
          <>
            {/* Price input */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-4">
              <label className="flex items-center gap-1.5 text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2">
                <PoundSterling size={14} />
                Default price for new scans
              </label>
              <div className="flex items-center gap-1">
                <span className="text-2xl font-bold text-amber-900 dark:text-amber-300">£</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  placeholder="0.00"
                  className="flex-1 text-3xl font-bold bg-transparent border-b-2 border-amber-400 focus:border-amber-600 outline-none text-amber-900 dark:text-amber-300 pb-0.5 min-w-0"
                />
              </div>
              <p className="text-xs text-amber-600 mt-2">
                Set a default price before scanning. You can adjust per-product in the editor.
              </p>
            </div>

            {/* Camera */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => cameraOn ? stopCamera() : void startCamera()}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold ${
                    cameraOn
                      ? "bg-red-100 text-red-700 border border-red-200"
                      : "bg-[#1e3a5f] text-white"
                  }`}
                >
                  {cameraOn ? <><CameraOff size={16} /> Stop Camera</> : <><Camera size={16} /> Start Camera</>}
                </button>
                {cameraOn && !scanning && <span className="text-sm text-green-600 font-medium">● Ready</span>}
                {cameraOn && scanning && (
                  <span className="flex items-center gap-1.5 text-sm text-gray-400">
                    <Loader2 size={14} className="animate-spin" /> Looking up…
                  </span>
                )}
              </div>

              {/* Video element always in DOM so ZXing can access it before setCameraOn fires */}
              <div className={`relative rounded-xl overflow-hidden bg-black aspect-video${cameraOn ? "" : " hidden"}`}>
                <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="border-2 border-white/70 rounded-lg w-3/4 h-1/3" />
                </div>
                {priceInput && (
                  <div className="absolute bottom-3 right-3 bg-black/60 text-white text-sm font-bold px-3 py-1.5 rounded-lg">
                    £{priceInput}
                  </div>
                )}
                {scanning && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <Loader2 size={32} className="animate-spin text-white" />
                  </div>
                )}
              </div>
            </div>

            {/* Manual / USB input */}
            <form onSubmit={onManualSubmit} className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700">
                Or type / USB scanner
              </label>
              <div className="flex gap-2">
                <input
                  ref={manualRef}
                  type="text"
                  inputMode="numeric"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  placeholder="Type or scan barcode…"
                  className="flex-1 border border-gray-300 rounded-xl px-3 py-2.5 text-sm font-mono"
                  disabled={scanning}
                />
                <button
                  type="submit"
                  disabled={!manualInput.trim() || scanning}
                  className="px-4 py-2.5 bg-[#1e3a5f] text-white rounded-xl text-sm font-semibold disabled:opacity-40"
                >
                  Add
                </button>
              </div>
            </form>

            {/* Results log */}
            {log.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">
                    {foundCount} added · {log.filter((r) => r.status === "notfound").length} not found
                  </span>
                  <button onClick={() => setLog([])} className="text-xs text-gray-400 hover:text-gray-600">Clear</button>
                </div>
                <div className="space-y-1.5 max-h-72 overflow-y-auto">
                  {log.map((r, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm ${
                        r.status === "found"    ? "bg-green-50 border border-green-200" :
                        r.status === "already"  ? "bg-blue-50 dark:bg-blue-900/20 border border-blue-200" :
                        r.status === "notfound" ? "bg-amber-50 dark:bg-amber-900/20 border border-amber-200" :
                                                  "bg-red-50 border border-red-200"
                      }`}
                    >
                      {r.status === "found" || r.status === "already"
                        ? <CheckCircle size={15} className={r.status === "found" ? "text-green-600 shrink-0" : "text-blue-500 shrink-0"} />
                        : <XCircle size={15} className={r.status === "notfound" ? "text-amber-500 shrink-0" : "text-red-500 shrink-0"} />
                      }
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-gray-800">
                          {r.productName ?? <span className="font-mono text-xs text-gray-400">{r.barcode}</span>}
                        </span>
                        <span className="text-xs text-gray-400 ml-2">
                          {r.status === "found" ? "added" : r.status === "already" ? "already in store" : r.status === "notfound" ? "not in catalogue" : "error"}
                          {r.section ? ` · ${r.section}` : ""}
                        </span>
                      </div>
                      {r.pricePence != null && r.pricePence > 0 ? (
                        <span className="text-sm font-semibold text-gray-700 shrink-0">£{(r.pricePence / 100).toFixed(2)}</span>
                      ) : (
                        <span className="text-xs text-gray-300 shrink-0">no price</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Quick Save card — shown for known or OFD-found products */}
      {quickCard !== null && selectedStoreId && token && (
        <QuickCard
          card={quickCard}
          storeId={selectedStoreId}
          token={token}
          onSave={handleQuickCardSave}
          onEdit={handleQuickCardEdit}
        />
      )}

      {/* Editor overlay — shown for completely unknown products or when "Edit Details" tapped */}
      {editor !== null && selectedStoreId && (
        <ProductEditor
          editor={editor}
          storeId={selectedStoreId}
          token={token}
          onClose={handleEditorClose}
          onProductCreated={handleProductCreated}
        />
      )}

      {/* Skeleton while looking up barcode */}
      {editorLoading !== null && (
        <EditorSkeleton barcode={editorLoading} />
      )}
    </div>
  );
}
